// Kowloon — dense architectural ink drawing on parchment, driven by price data
//
// Three engines:
//   Line engine   – micro-jitter on all strokes, varied weight/opacity
//   Hatching engine – horiz / vert / diagonal / cross / dots, clipped per face
//   Layout engine – column-stacked building modules; shed / gable / dome / spire
//                   roofs; full X-brace scaffolding panels on flanks

// ── Layout constants ──────────────────────────────────────────────────────────
const GROUND_Y_FRAC = 1.0;
const CITY_TOP_MIN = 0.02; // at max price — tallest peak (fraction of H)
const CITY_TOP_MAX = 0.92; // at min price — shortest point
const COL_W_MIN = 20; // building column width (px)
const COL_W_MAX = 54;
const MOD_H_MIN = 14; // module (one story-block) height
const MOD_H_MAX = 44;
const ROOF_H_MIN = 6;
const ROOF_H_MAX = 20;
const OVERHANG_MAX = 8; // max px a building overhangs its column
const SMOOTHING_BLEND = 0.65;
const SMOOTHING_PASS = 3;

// ── Texture + roof type pools ─────────────────────────────────────────────────
// 0=horiz lines  1=vert lines  2=diag  3=cross  4=dots  5=plain
const TEX_POOL = [0, 0, 0, 0, 0, 1, 2, 3, 4, 5];

// 0=flat  1=shed-R  2=shed-L  3=gable  4=dome  5=spire
const ROOF_POOL = [1, 1, 2, 2, 3, 3, 4, 4, 5, 0, 0, 0];

// ── Paper + ink constants ─────────────────────────────────────────────────────
const FIBER_COUNT = 2800;
const GRAIN_COUNT = 4000;
const WIRE_MIN = 4;
const WIRE_MAX = 12;

const PAPER = [240, 232, 216];
const INK = [22, 16, 10];
const INK2 = [82, 66, 52];

const _hex = (r, g, b) =>
    '#' + [r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('');

const _smooth = (prices, passes, blend) => {
    const k = [1, 2, 3, 2, 1],
        kS = 9,
        kC = 2;
    let arr = [...prices];
    for (let pass = 0; pass < passes; pass++) {
        const prev = [...arr];
        arr = prev.map((v, i) => {
            if (i === 0 || i === prev.length - 1) return v;
            const sm =
                k.reduce((s, w, ki) => {
                    const pi = Math.max(
                        0,
                        Math.min(prev.length - 1, i + ki - kC)
                    );
                    return s + prev[pi] * w;
                }, 0) / kS;
            return v * (1 - blend) + sm * blend;
        });
    }
    return arr;
};

// Tiny line-engine jitter — deterministic so draw is reproducible
const _jit = (seed, amt) => Math.sin(seed * 137.508 + 0.5) * amt;

export const kowloonCity = (p, chartData) => {
    let buildings = [];
    let scaffolds = [];
    let wires = [];
    let paperFibers = [];
    let paperGrain = [];
    let bgFar = []; // distant silhouette layer
    let bgMid = []; // mid-distance buildings with minimal texture

    p.setup = () => {
        p.createCanvas(p.windowWidth, p.windowHeight);
        const W = p.width,
            H = p.height;
        const groundY = H * GROUND_Y_FRAC;

        // ── Paper texture ─────────────────────────────────────────────────
        for (let i = 0; i < FIBER_COUNT; i++) {
            paperFibers.push({
                x: p.random(0, W),
                y: p.random(0, H),
                len: p.random(4, 22),
                angle: p.random(-0.18, 0.18),
                alpha: p.random(0.025, 0.1),
            });
        }
        for (let i = 0; i < GRAIN_COUNT; i++) {
            paperGrain.push({
                x: p.random(0, W),
                y: p.random(0, H),
                dark: p.random() > 0.68,
                alpha: p.random(0.04, 0.22),
            });
        }

        // ── Price → city silhouette ───────────────────────────────────────
        const vals = chartData?.values || [];
        if (!vals.length) {
            p.noLoop();
            return;
        }

        const closes = vals
            .map((v) => parseFloat(v.close))
            .filter(Number.isFinite);
        const smooth = _smooth(closes, SMOOTHING_PASS, SMOOTHING_BLEND);
        const minP = Math.min(...smooth);
        const maxP = Math.max(...smooth);
        const priceR = maxP - minP || 1;
        const N = closes.length;

        const cityTopAt = (x) => {
            const frac = Math.max(0, Math.min(1, x / W));
            const fi = frac * (N - 1);
            const i0 = Math.floor(fi);
            const i1 = Math.min(i0 + 1, N - 1);
            const t = fi - i0;
            const ph0 = (smooth[i0] - minP) / priceR;
            const ph1 = (smooth[i1] - minP) / priceR;
            const ph = ph0 + (ph1 - ph0) * t;
            return H * (CITY_TOP_MAX - (CITY_TOP_MAX - CITY_TOP_MIN) * ph);
        };

        // ── Building columns ──────────────────────────────────────────────
        // Track occupied ground slots for scaffold placement later
        const columnEdges = [];

        let cx = 0;
        while (cx < W) {
            const colW = p.random(COL_W_MIN, COL_W_MAX);
            const centreX = cx + colW / 2;
            const topY = cityTopAt(centreX);
            columnEdges.push({ x: cx, w: colW, topY });

            let curY = groundY;
            while (curY > topY + MOD_H_MIN) {
                const rawH = p.random(MOD_H_MIN, MOD_H_MAX);
                const modH = Math.min(rawH, curY - topY);
                const oh = p.random(-OVERHANG_MAX, OVERHANG_MAX);
                const bx = Math.max(0, cx + oh / 2);
                const bw = Math.max(8, colW - oh);
                const isTop = curY - modH <= topY + MOD_H_MIN * 0.8;

                const texture = TEX_POOL[Math.floor(p.random(TEX_POOL.length))];
                const roofType = isTop
                    ? ROOF_POOL[Math.floor(p.random(ROOF_POOL.length))]
                    : 0;
                const roofH =
                    roofType !== 0 ? p.random(ROOF_H_MIN, ROOF_H_MAX) : 0;
                const lineSpac = p.random(2.4, 4.2);

                // For spires, enforce narrow column
                const actualBw = roofType === 5 ? Math.min(bw, 28) : bw;

                buildings.push({
                    x: bx,
                    y: curY - modH,
                    w: actualBw,
                    h: modH,
                    texture,
                    lineSpac,
                    roofType,
                    roofH,
                    seedX: Math.floor(bx * 0.1 + curY * 0.07),
                });

                curY -= modH;
            }

            cx += colW;
        }

        // ── Scaffold panels ───────────────────────────────────────────────
        // Full X-brace panels on the outer flanks and some interior spots
        const scaffoldPositions = [];

        // Left flank
        if (columnEdges.length > 0) {
            const lc = columnEdges[0];
            const lTop = cityTopAt(lc.x + lc.w / 2);
            scaffoldPositions.push({
                x: lc.x - p.random(10, 30),
                y: lTop - p.random(5, 20),
                w: p.random(30, 55),
                h: groundY - lTop + p.random(5, 20),
            });
        }
        // Right flank
        if (columnEdges.length > 0) {
            const rc = columnEdges[columnEdges.length - 1];
            const rTop = cityTopAt(rc.x + rc.w / 2);
            scaffoldPositions.push({
                x: rc.x + rc.w - p.random(5, 15),
                y: rTop - p.random(5, 20),
                w: p.random(30, 55),
                h: groundY - rTop + p.random(5, 20),
            });
        }
        // 1-2 interior scaffold panels at steep sections
        const interiorCount = Math.floor(p.random(1, 3));
        for (let i = 0; i < interiorCount; i++) {
            const selIdx = Math.floor(p.random(2, columnEdges.length - 2));
            if (selIdx >= 0 && selIdx < columnEdges.length) {
                const ic = columnEdges[selIdx];
                const iTop = cityTopAt(ic.x + ic.w / 2);
                scaffoldPositions.push({
                    x: ic.x,
                    y: iTop,
                    w: p.random(28, 50),
                    h: groundY - iTop,
                });
            }
        }
        scaffolds = scaffoldPositions;

        // ── Background city backdrop ──────────────────────────────────────
        // Far layer: fully randomised silhouette — no price correlation.
        // Heights cluster around the mid-canvas with occasional towers.
        let bfx = 0;
        while (bfx < W) {
            const bfw = p.random(12, 62);
            const isTower = p.random() < 0.12;
            const bfh = isTower
                ? H * p.random(0.25, 0.52)
                : H * p.random(0.04, 0.28);
            const roofKind = isTower
                ? 'spire'
                : p.random() < 0.35
                  ? 'gable'
                  : 'flat';
            bgFar.push({ x: bfx, w: bfw, topY: groundY - bfh, roofKind });
            bfx += bfw;
        }

        // Mid layer: loosely tracks the price silhouette but set ~10-20% lower,
        // giving depth without competing with the foreground city.
        let bmx = 0;
        while (bmx < W) {
            const bmw = p.random(16, 50);
            const priceTop = cityTopAt(bmx + bmw / 2);
            const offset = H * p.random(0.06, 0.18);
            const bmTop = priceTop + offset;
            if (bmTop < groundY - 8) {
                bgMid.push({
                    x: bmx,
                    w: bmw,
                    topY: bmTop,
                    lineSpac: p.random(3.8, 6.0),
                    seed: Math.floor(bmx * 0.17 + bmTop * 0.09),
                });
            }
            bmx += bmw;
        }

        // ── Sky wires — anchored to actual rooftop corners ────────────────
        // Collect attachment points from the top of each building column
        // (only buildings that have a roof shape, i.e. top of their stack).
        const attachPts = [];
        buildings.forEach(({ x, y, w, roofType, roofH }) => {
            if (roofType === 0) return;
            const ov = Math.min(w * 0.07, 5);
            attachPts.push({ x: x - ov, y }); // left eave
            attachPts.push({ x: x + w + ov, y }); // right eave
            if (roofType === 3)
                // gable peak
                attachPts.push({ x: x + w / 2, y: y - roofH });
            if (roofType === 5)
                // spire tip
                attachPts.push({ x: x + w / 2, y: y - roofH * 2.2 });
        });

        const wireCount = Math.floor(p.random(WIRE_MIN, WIRE_MAX));
        for (let i = 0; i < wireCount; i++) {
            if (attachPts.length < 2) break;

            const i1 = Math.floor(p.random(attachPts.length));
            const pt1 = attachPts[i1];

            // Second point: must be horizontally separated, not too close or far
            const pool = [];
            for (let j = 0; j < attachPts.length; j++) {
                if (j === i1) continue;
                const dx = Math.abs(attachPts[j].x - pt1.x);
                if (dx > W * 0.05 && dx < W * 0.55) pool.push(attachPts[j]);
            }
            if (!pool.length) continue;

            const pt2 = pool[Math.floor(p.random(pool.length))];
            const dx = Math.abs(pt2.x - pt1.x);
            // Droop scales with span so long wires sag more
            const droop = dx * p.random(0.04, 0.14);
            wires.push({ x1: pt1.x, y1: pt1.y, x2: pt2.x, y2: pt2.y, droop });
        }

        // ── Palette ───────────────────────────────────────────────────────
        p._generatedPalette = [
            _hex(...PAPER),
            '#e0d4bc',
            '#c8bca4',
            '#a89880',
            _hex(...INK2),
            '#3c2c1e',
            _hex(...INK),
            '#d4c8b0',
        ];

        p.noLoop();
    };

    // ─────────────────────────────────────────────────────────────────────────
    p.draw = () => {
        const ctx = p.drawingContext;
        const W = p.width,
            H = p.height;
        const groundY = H * GROUND_Y_FRAC;

        // Paper base
        ctx.fillStyle = `rgb(${PAPER[0]},${PAPER[1]},${PAPER[2]})`;
        ctx.fillRect(0, 0, W, H);

        // Paper fibres
        ctx.lineWidth = 0.4;
        paperFibers.forEach(({ x, y, len, angle, alpha }) => {
            ctx.globalAlpha = alpha;
            ctx.strokeStyle = 'rgb(185,174,156)';
            ctx.beginPath();
            ctx.moveTo(x, y);
            ctx.lineTo(x + Math.cos(angle) * len, y + Math.sin(angle) * len);
            ctx.stroke();
        });
        ctx.globalAlpha = 1;

        // Grain dots
        paperGrain.forEach(({ x, y, dark, alpha }) => {
            ctx.fillStyle = dark
                ? `rgba(155,142,124,${alpha})`
                : `rgba(215,206,190,${alpha})`;
            ctx.fillRect(x, y, 1, 1);
        });

        // Vignette
        const vig = ctx.createRadialGradient(
            W / 2,
            H / 2,
            H * 0.28,
            W / 2,
            H / 2,
            H * 0.82
        );
        vig.addColorStop(0, 'rgba(0,0,0,0)');
        vig.addColorStop(1, 'rgba(60,40,20,0.13)');
        ctx.fillStyle = vig;
        ctx.fillRect(0, 0, W, H);

        // ── Far backdrop silhouette ───────────────────────────────────────
        bgFar.forEach(({ x, w, topY, roofKind }) => {
            const h = groundY - topY;

            // Faint filled mass
            ctx.fillStyle = 'rgba(198,188,170,0.28)';
            ctx.fillRect(x, topY, w, h);

            // Very faint wall lines
            ctx.strokeStyle = `rgba(${INK[0]},${INK[1]},${INK[2]},0.06)`;
            ctx.lineWidth = 0.5;
            ctx.beginPath();
            ctx.moveTo(x, groundY);
            ctx.lineTo(x, topY);
            ctx.lineTo(x + w, topY);
            ctx.lineTo(x + w, groundY);
            ctx.stroke();

            // Simple roof shape (no fill/texture — outline only)
            if (roofKind === 'gable') {
                ctx.strokeStyle = `rgba(${INK[0]},${INK[1]},${INK[2]},0.10)`;
                ctx.beginPath();
                const ph = Math.min(h * 0.22, 18);
                ctx.moveTo(x, topY);
                ctx.lineTo(x + w / 2, topY - ph);
                ctx.lineTo(x + w, topY);
                ctx.stroke();
            } else if (roofKind === 'spire') {
                ctx.strokeStyle = `rgba(${INK[0]},${INK[1]},${INK[2]},0.10)`;
                ctx.beginPath();
                const sh = Math.min(w * 1.2, 40);
                ctx.moveTo(x + w * 0.3, topY);
                ctx.lineTo(x + w / 2, topY - sh);
                ctx.lineTo(x + w * 0.7, topY);
                ctx.stroke();
            }
        });

        // ── Mid backdrop buildings ─────────────────────────────────────────
        bgMid.forEach(({ x, w, topY, lineSpac, seed }) => {
            const h = groundY - topY;

            // Slightly more visible fill
            ctx.fillStyle = 'rgba(182,171,153,0.30)';
            ctx.fillRect(x, topY, w, h);

            // Faint horizontal line texture
            ctx.strokeStyle = `rgba(${INK[0]},${INK[1]},${INK[2]},0.09)`;
            ctx.lineWidth = 0.5;
            ctx.beginPath();
            for (let ly = topY + lineSpac; ly < topY + h - 1; ly += lineSpac) {
                ctx.moveTo(x, ly);
                ctx.lineTo(x + w, ly);
            }
            ctx.stroke();

            // Scattered tiny window dots
            ctx.fillStyle = `rgba(${INK[0]},${INK[1]},${INK[2]},0.18)`;
            const dotSpacX = Math.max(
                (w - 4) / Math.max(1, Math.floor(w / 10)),
                8
            );
            const dotSpacY = 10;
            for (let dy = topY + 6; dy < topY + h - 4; dy += dotSpacY) {
                for (let dx = x + 4; dx < x + w - 3; dx += dotSpacX) {
                    const vis =
                        Math.abs(
                            Math.sin(seed * 37.1 + dy * 0.31 + dx * 0.19)
                        ) > 0.52;
                    if (vis) ctx.fillRect(dx, dy, 2, 2);
                }
            }

            // Faint outline
            ctx.strokeStyle = `rgba(${INK[0]},${INK[1]},${INK[2]},0.14)`;
            ctx.lineWidth = 0.6;
            ctx.beginPath();
            ctx.moveTo(x, groundY);
            ctx.lineTo(x, topY);
            ctx.lineTo(x + w, topY);
            ctx.lineTo(x + w, groundY);
            ctx.stroke();
        });

        // Sky wires (behind buildings)
        ctx.strokeStyle = `rgba(${INK[0]},${INK[1]},${INK[2]},0.28)`;
        ctx.lineWidth = 0.5;
        wires.forEach(({ x1, y1, x2, y2, droop }) => {
            const mx = (x1 + x2) / 2;
            const my = Math.max(y1, y2) + droop;
            ctx.beginPath();
            ctx.moveTo(x1, y1);
            ctx.quadraticCurveTo(mx, my, x2, y2);
            ctx.stroke();
        });

        // Buildings
        buildings.forEach((b) => _drawBuilding(ctx, b));

        // Scaffolding (drawn on top of buildings)
        scaffolds.forEach((s) => _drawScaffold(ctx, s));
    };

    // ── Draw one building module ──────────────────────────────────────────────
    function _drawBuilding(
        ctx,
        { x, y, w, h, texture, lineSpac, roofType, roofH, seedX }
    ) {
        // Body fill texture
        _bodyTex(ctx, x, y, w, h, texture, lineSpac);

        // Facade details (drawn on top of texture, under outline)
        _drawWindows(ctx, x, y, w, h, seedX);
        _drawLedges(ctx, x, y, w, h, seedX);
        _drawPipes(ctx, x, y, w, h, seedX);
        _drawAcUnits(ctx, x, y, w, h, seedX);

        // Body outline with subtle line-engine jitter
        const jx = _jit(seedX, 0.4);
        const jy = _jit(seedX + 1, 0.4);
        ctx.strokeStyle = `rgba(${INK[0]},${INK[1]},${INK[2]},0.76)`;
        ctx.lineWidth = 1.0;
        ctx.beginPath();
        ctx.moveTo(x + jx, y + h + jy);
        ctx.lineTo(x + jx, y + jy);
        ctx.lineTo(x + w + jx, y + jy);
        ctx.lineTo(x + w + jx, y + h + jy);
        ctx.stroke();

        // Roof
        if (roofH > 0 && roofType !== 0) {
            _drawRoof(ctx, x, y, w, roofType, roofH, seedX);
        }
    }

    // ── Small rectangular windows ─────────────────────────────────────────────
    function _drawWindows(ctx, bx, by, bw, bh, seed) {
        if (bw < 12 || bh < 16) return;

        const winW = Math.min(bw * 0.18, 5);
        const winH = Math.min(bh * 0.2, 6);
        const rowH = 11;
        const rows = Math.max(1, Math.floor((bh - 4) / rowH));
        const cols = Math.max(1, Math.floor(bw / (winW + 4)));
        const colW = bw / cols;

        ctx.lineWidth = 0.5;

        for (let r = 0; r < rows; r++) {
            for (let c = 0; c < cols; c++) {
                const hash = Math.abs(
                    Math.sin(seed * 41.3 + r * 13.7 + c * 9.1)
                );
                if (hash > 0.58) continue;

                const wx = bx + (c + 0.5) * colW - winW / 2;
                const wy = by + (r + 0.5) * rowH + 2;
                if (wy + winH > by + bh - 1) continue;

                ctx.fillStyle = `rgba(${INK[0]},${INK[1]},${INK[2]},0.60)`;
                ctx.fillRect(wx, wy, winW, winH);

                ctx.strokeStyle = `rgba(${INK[0]},${INK[1]},${INK[2]},0.82)`;
                ctx.beginPath();
                ctx.rect(wx, wy, winW, winH);
                ctx.stroke();

                // Horizontal divider on wider windows
                if (winW > 4) {
                    ctx.beginPath();
                    ctx.moveTo(wx, wy + winH * 0.5);
                    ctx.lineTo(wx + winW, wy + winH * 0.5);
                    ctx.stroke();
                }
            }
        }
    }

    // ── Cornices / ledges projecting beyond wall face ─────────────────────────
    function _drawLedges(ctx, bx, by, bw, bh, seed) {
        if (bh < 18) return;

        const slots = Math.floor(bh / 14);
        ctx.lineWidth = 1.0;

        for (let i = 1; i < slots; i++) {
            const hash = Math.abs(Math.sin(seed * 57.1 + i * 19.3));
            if (hash > 0.28) continue;

            const ly = by + i * (bh / slots);
            const ext = 2 + Math.abs(Math.sin(seed * 23.1 + i * 8.7)) * 4;

            ctx.strokeStyle = `rgba(${INK[0]},${INK[1]},${INK[2]},0.68)`;
            ctx.beginPath();
            ctx.moveTo(bx - ext, ly);
            ctx.lineTo(bx + bw + ext, ly);
            ctx.stroke();

            // Small vertical returns at each end
            ctx.lineWidth = 0.7;
            ctx.beginPath();
            ctx.moveTo(bx - ext, ly);
            ctx.lineTo(bx - ext, ly + 2.5);
            ctx.moveTo(bx + bw + ext, ly);
            ctx.lineTo(bx + bw + ext, ly + 2.5);
            ctx.stroke();
            ctx.lineWidth = 1.0;
        }
    }

    // ── Vertical drain / conduit pipe on one building edge ────────────────────
    function _drawPipes(ctx, bx, by, bw, bh, seed) {
        if (bh < 24 || bw < 10) return;

        const hash = Math.abs(Math.sin(seed * 71.3));
        if (hash > 0.3) return;

        const onLeft = Math.sin(seed * 43.7) > 0;
        const px = onLeft ? bx + 2.5 : bx + bw - 2.5;

        ctx.strokeStyle = `rgba(${INK[0]},${INK[1]},${INK[2]},0.55)`;
        ctx.lineWidth = 1.3;
        ctx.beginPath();
        ctx.moveTo(px, by);
        ctx.lineTo(px, by + bh);
        ctx.stroke();

        // Joint rings every ~18 px
        ctx.lineWidth = 0.6;
        ctx.beginPath();
        for (let jy = by + 10; jy < by + bh - 4; jy += 18) {
            ctx.moveTo(px - 2.5, jy);
            ctx.lineTo(px + 2.5, jy);
        }
        ctx.stroke();
    }

    // ── AC / vent units protruding from wall ──────────────────────────────────
    function _drawAcUnits(ctx, bx, by, bw, bh, seed) {
        if (bw < 14 || bh < 14) return;

        const unitH = 4 + Math.abs(Math.sin(seed * 37.1)) * 3;
        const unitW = 6 + Math.abs(Math.sin(seed * 51.3)) * 5;
        const slots = Math.floor(bh / 16);

        for (let i = 0; i < slots; i++) {
            const hash = Math.abs(Math.sin(seed * 83.1 + i * 21.7));
            if (hash > 0.18) continue;

            const uy = by + i * 16 + 5;
            if (uy + unitH > by + bh - 2) continue;

            const onLeft = Math.sin(seed * 67.3 + i * 11.1) > 0;
            const ux = onLeft ? bx - unitW + 1 : bx + bw - 1;

            // Unit body (paper fill so it reads as a protrusion)
            ctx.fillStyle = `rgba(${PAPER[0]},${PAPER[1]},${PAPER[2]},0.92)`;
            ctx.fillRect(ux, uy, unitW, unitH);

            ctx.strokeStyle = `rgba(${INK[0]},${INK[1]},${INK[2]},0.72)`;
            ctx.lineWidth = 0.6;
            ctx.beginPath();
            ctx.rect(ux, uy, unitW, unitH);
            ctx.stroke();

            // Vent slats
            ctx.beginPath();
            for (let vy = uy + 1.5; vy < uy + unitH - 0.5; vy += 1.5) {
                ctx.moveTo(ux + 0.5, vy);
                ctx.lineTo(ux + unitW - 0.5, vy);
            }
            ctx.stroke();
        }
    }

    // ── Facade texture ────────────────────────────────────────────────────────
    function _bodyTex(ctx, x, y, w, h, type, spacing) {
        if (type === 5 || w < 4 || h < 4) return;

        const inkA = `rgba(${INK[0]},${INK[1]},${INK[2]},0.48)`;

        ctx.strokeStyle = inkA;
        ctx.lineWidth = 0.5;
        ctx.beginPath();

        if (type === 0) {
            // horizontal lines
            for (let ly = y + spacing; ly < y + h - 1; ly += spacing) {
                ctx.moveTo(x + 0.5, ly);
                ctx.lineTo(x + w - 0.5, ly);
            }
        } else if (type === 1) {
            // vertical lines
            for (let lx = x + spacing; lx < x + w - 1; lx += spacing) {
                ctx.moveTo(lx, y + 0.5);
                ctx.lineTo(lx, y + h - 0.5);
            }
        } else {
            // Diagonal / cross / dots all need clip
            ctx.save();
            ctx.beginPath();
            ctx.rect(x, y, w, h);
            ctx.clip();
            ctx.strokeStyle = inkA;
            ctx.lineWidth = 0.5;
            ctx.beginPath();
            if (type === 2) {
                for (let t = -(h + spacing); t < w + h; t += spacing * 1.2) {
                    ctx.moveTo(x + t, y);
                    ctx.lineTo(x + t + h, y + h);
                }
            } else if (type === 3) {
                const sp = spacing * 1.4;
                for (let t = -(h + sp); t < w + h; t += sp) {
                    ctx.moveTo(x + t, y);
                    ctx.lineTo(x + t + h, y + h);
                    ctx.moveTo(x + t + h, y);
                    ctx.lineTo(x + t, y + h);
                }
            } else if (type === 4) {
                const ds = spacing * 1.6;
                for (let ly = y + ds; ly < y + h; ly += ds) {
                    for (let lx = x + ds; lx < x + w; lx += ds) {
                        ctx.moveTo(lx, ly);
                        ctx.arc(lx, ly, 0.9, 0, Math.PI * 2);
                    }
                }
            }
            ctx.stroke();
            ctx.restore();
            return;
        }
        ctx.stroke();
    }

    // ── Roof shapes ───────────────────────────────────────────────────────────
    function _drawRoof(ctx, x, y, w, roofType, roofH, seed) {
        const jx = _jit(seed + 2, 0.35);
        const jy = _jit(seed + 3, 0.35);
        const ink = `rgba(${INK[0]},${INK[1]},${INK[2]},0.80)`;
        const ov = Math.min(w * 0.07, 5); // eave overhang

        if (roofType === 1 || roofType === 2) {
            // Shed roof — one side higher
            const highY = y - roofH;
            const lowY = y - roofH * 0.12;
            const lY = roofType === 1 ? highY : lowY;
            const rY = roofType === 1 ? lowY : highY;

            // Clipped fill (horizontal lines inside the shed parallelogram)
            ctx.save();
            ctx.beginPath();
            ctx.moveTo(x - ov, y);
            ctx.lineTo(x - ov, lY);
            ctx.lineTo(x + w + ov, rY);
            ctx.lineTo(x + w + ov, y);
            ctx.closePath();
            ctx.clip();

            ctx.strokeStyle = `rgba(${INK[0]},${INK[1]},${INK[2]},0.40)`;
            ctx.lineWidth = 0.5;
            ctx.beginPath();
            for (let ly = Math.min(lY, rY) + 3; ly < y; ly += 3) {
                ctx.moveTo(x - ov, ly);
                ctx.lineTo(x + w + ov, ly);
            }
            ctx.stroke();
            ctx.restore();

            // Outline
            ctx.strokeStyle = ink;
            ctx.lineWidth = 1.0;
            ctx.beginPath();
            ctx.moveTo(x - ov + jx, y + jy);
            ctx.lineTo(x - ov + jx, lY + jy);
            ctx.lineTo(x + w + ov + jx, rY + jy);
            ctx.lineTo(x + w + ov + jx, y + jy);
            ctx.stroke();
        } else if (roofType === 3) {
            // Gable — peaked in centre
            const peak = y - roofH;
            const cx = x + w / 2;

            ctx.save();
            ctx.beginPath();
            ctx.moveTo(x - ov, y);
            ctx.lineTo(cx, peak);
            ctx.lineTo(x + w + ov, y);
            ctx.closePath();
            ctx.clip();

            ctx.strokeStyle = `rgba(${INK[0]},${INK[1]},${INK[2]},0.40)`;
            ctx.lineWidth = 0.5;
            ctx.beginPath();
            for (let ly = peak + 3; ly < y; ly += 3) {
                ctx.moveTo(x - ov, ly);
                ctx.lineTo(x + w + ov, ly);
            }
            ctx.stroke();
            ctx.restore();

            ctx.strokeStyle = ink;
            ctx.lineWidth = 1.0;
            ctx.beginPath();
            ctx.moveTo(x - ov + jx, y + jy);
            ctx.lineTo(cx + jx, peak + jy);
            ctx.lineTo(x + w + ov + jx, y + jy);
            ctx.stroke();
        } else if (roofType === 4) {
            // Dome — semi-ellipse
            const centerX = x + w / 2;
            const rx = w / 2 + ov;
            const ry = roofH;

            ctx.save();
            ctx.beginPath();
            ctx.ellipse(centerX, y, rx, ry, 0, Math.PI, 0);
            ctx.closePath();
            ctx.clip();

            ctx.strokeStyle = `rgba(${INK[0]},${INK[1]},${INK[2]},0.40)`;
            ctx.lineWidth = 0.5;
            ctx.beginPath();
            for (let ly = y - ry + 4; ly < y; ly += 3.5) {
                // Clip limits the line to inside the ellipse
                ctx.moveTo(centerX - rx, ly);
                ctx.lineTo(centerX + rx, ly);
            }
            ctx.stroke();
            ctx.restore();

            ctx.strokeStyle = ink;
            ctx.lineWidth = 1.0;
            ctx.beginPath();
            ctx.ellipse(centerX + jx, y + jy, rx, ry, 0, Math.PI, 0);
            ctx.stroke();
        } else if (roofType === 5) {
            // Spire — narrow pointed tower
            const spireW = Math.min(w * 0.35, 10);
            const cx = x + w / 2;
            const peak = y - roofH * 2.2;

            ctx.strokeStyle = ink;
            ctx.lineWidth = 1.0;
            ctx.beginPath();
            ctx.moveTo(cx - spireW / 2 + jx, y + jy);
            ctx.lineTo(cx + jx, peak + jy);
            ctx.lineTo(cx + spireW / 2 + jx, y + jy);
            ctx.stroke();

            // Narrow horizontal lines within spire
            ctx.save();
            ctx.beginPath();
            ctx.moveTo(cx - spireW / 2, y);
            ctx.lineTo(cx, peak);
            ctx.lineTo(cx + spireW / 2, y);
            ctx.closePath();
            ctx.clip();

            ctx.strokeStyle = `rgba(${INK[0]},${INK[1]},${INK[2]},0.38)`;
            ctx.lineWidth = 0.5;
            ctx.beginPath();
            for (let ly = peak + 4; ly < y; ly += 3.5) {
                ctx.moveTo(cx - spireW / 2, ly);
                ctx.lineTo(cx + spireW / 2, ly);
            }
            ctx.stroke();
            ctx.restore();
        }
    }

    // ── Scaffold panel — full X-brace per cell ────────────────────────────────
    function _drawScaffold(ctx, { x, y, w, h }) {
        if (w < 4 || h < 4) return;

        const cellW = 16;
        const cellH = 16;
        const cols = Math.max(1, Math.ceil(w / cellW));
        const rows = Math.max(1, Math.ceil(h / cellH));
        const right = x + cols * cellW;
        const bot = y + rows * cellH;

        // Frame: poles + rails
        ctx.strokeStyle = `rgba(${INK[0]},${INK[1]},${INK[2]},0.52)`;
        ctx.lineWidth = 0.7;
        ctx.beginPath();

        for (let c = 0; c <= cols; c++) {
            const px = x + c * cellW;
            ctx.moveTo(px, y);
            ctx.lineTo(px, bot);
        }
        for (let r = 0; r <= rows; r++) {
            const ry = y + r * cellH;
            ctx.moveTo(x, ry);
            ctx.lineTo(right, ry);
        }
        ctx.stroke();

        // X braces in every cell (full cross — not alternating)
        ctx.strokeStyle = `rgba(${INK[0]},${INK[1]},${INK[2]},0.42)`;
        ctx.lineWidth = 0.55;
        ctx.beginPath();
        for (let c = 0; c < cols; c++) {
            for (let r = 0; r < rows; r++) {
                const x1 = x + c * cellW;
                const x2 = x + (c + 1) * cellW;
                const y1 = y + r * cellH;
                const y2 = y + (r + 1) * cellH;
                ctx.moveTo(x1, y1);
                ctx.lineTo(x2, y2);
                ctx.moveTo(x2, y1);
                ctx.lineTo(x1, y2);
            }
        }
        ctx.stroke();
    }
};
